/**
 * RegionalPhotoboothHirePage ✦ iter222i
 * ─────────────────────────────────────────────────────────────
 * SEO-anchored regional landing pages — currently mounted at:
 *   • /photobooth-hire-cheshire
 *   • /photobooth-hire-north-wales
 *
 * Each route renders the same surface, parameterised by the
 * `regionKey` prop. The region config drives:
 *   • document.title and <meta name="description">
 *   • the hero headline + lede
 *   • the coverage-area strap (named towns)
 *   • a "Venues we attend in {region}" strip filtered to
 *     photographed venues whose seed `region` field OR whose
 *     `location` substring matches the regional aliases
 *
 * Below the regional strip the page renders the same packages /
 * booth collection / add-ons / enquiry form as the main
 * /photobooth-hire page — single source of truth for prices and
 * inclusions. The fetch goes against the same backend endpoints,
 * which already merge in any admin-uploaded cover photos.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ServicesNav from '../components/services/ServicesNav';
import BrandedPlaceholder from '../components/services/BrandedPlaceholder';
import EnquiryForm from '../components/services/EnquiryForm';
import AvailabilityWidget from '../components/AvailabilityWidget';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_GHOST,
  GOLD, GOLD_MUTED, GOLD_FAINT, GOLD_HAIRLINE,
  WALNUT_CARD, PAGE_BG,
  PILL_BUTTON_PRIMARY, PILL_BUTTON_GHOST,
} from '../components/services/_tokens';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// iter222i — Region configs. Adding a new region is a single entry
// + a single <Route> in App.js. Aliases are matched case-insensitive
// against the venue `region` field AND `location` substring so
// admin-created venues (region: "") still flow into the right page.
const REGIONS = {
  cheshire: {
    slug: 'cheshire',
    name: 'Cheshire',
    title: 'Photobooth Hire in Cheshire — Chester, Knutsford, Macclesfield ✦ EventDrop',
    metaDescription:
      'Photobooth hire across Cheshire — Chester, Knutsford, Macclesfield, Tarporley, Nantwich. Infinity Mirror Booth, Selfie Pod and Audio Guestbook for weddings, corporate evenings and milestone celebrations. Check availability for your date.',
    headline: 'Photobooth Hire in Cheshire',
    eyebrow: '✦ Cheshire · North-West England',
    lede:
      'Photobooth hire for weddings, corporate evenings and milestone parties across Cheshire — Chester, Knutsford, Macclesfield, Tarporley, Nantwich and every village in between. Booths arrive with the right styling for the room.',
    towns: ['Chester', 'Knutsford', 'Macclesfield', 'Tarporley', 'Nantwich', 'Crewe', 'Northwich', 'Sandbach'],
    // iter222m — extend aliases so admin-typed venue locations like
    // "Knutsford" (no county) still bucket into the Cheshire page.
    aliases: ['cheshire', 'chester', 'knutsford', 'macclesfield', 'tarporley', 'nantwich', 'crewe', 'northwich', 'sandbach', 'tatton', 'colshaw'],
    venuesHeading: 'Venues we attend in Cheshire',
  },
  'north-wales': {
    slug: 'north-wales',
    name: 'North Wales',
    title: 'Photobooth Hire in North Wales — Wrexham, Llangollen, Anglesey ✦ EventDrop',
    metaDescription:
      'Photobooth hire across North Wales — Wrexham, Llangollen, Conwy, Anglesey and the Snowdonia border. Infinity Mirror Booth, Rustic Mirror Booth and Audio Guestbook for barn weddings, country-house weddings and corporate evenings.',
    headline: 'Photobooth Hire in North Wales',
    eyebrow: '✦ North Wales · Snowdonia border',
    lede:
      'Photobooth hire for weddings, proms and milestone evenings across North Wales — Wrexham, Llangollen, Conwy, Anglesey and the Snowdonia border. Barn-friendly booths, rustic styling, and the Audio Guestbook for the quieter moments.',
    towns: ['Wrexham', 'Llangollen', 'Conwy', 'Llandudno', 'Anglesey', 'Bangor', 'Caernarfon', 'Mold'],
    aliases: ['north-wales', 'north wales', 'wales', 'wrexham', 'llangollen', 'conwy', 'anglesey'],
    venuesHeading: 'Venues we attend in North Wales',
  },
  liverpool: {
    slug: 'liverpool',
    name: 'Liverpool',
    title: 'Photobooth Hire in Liverpool — Albert Dock, Aigburth, Crosby ✦ EventDrop',
    metaDescription:
      'Photobooth hire across Liverpool and the Merseyside city region — Albert Dock, Aigburth, Crosby, Allerton, Sefton Park. Infinity Mirror Booth, Selfie Pod and Audio Guestbook for city-centre weddings, corporate evenings and milestone parties.',
    headline: 'Photobooth Hire in Liverpool',
    eyebrow: '✦ Liverpool · Merseyside',
    lede:
      'Photobooth hire for city-centre weddings, dockside corporate evenings and milestone parties across Liverpool — Albert Dock, Aigburth, Crosby, Allerton and the wider Merseyside city region. Booths styled for the room, never the room styled around the booth.',
    towns: ['Liverpool City Centre', 'Albert Dock', 'Aigburth', 'Crosby', 'Allerton', 'Sefton Park', 'Woolton', 'Speke'],
    aliases: ['liverpool', 'merseyside', 'albert dock', 'aigburth', 'crosby'],
    venuesHeading: 'Venues we attend in Liverpool',
  },
  manchester: {
    slug: 'manchester',
    name: 'Manchester',
    title: 'Photobooth Hire in Manchester — Deansgate, Salford, Trafford ✦ EventDrop',
    metaDescription:
      'Photobooth hire across Greater Manchester — Deansgate, Salford Quays, Trafford, Didsbury, Altrincham. Infinity Mirror Booth, Selfie Pod and Audio Guestbook for city weddings, corporate evenings and milestone parties.',
    headline: 'Photobooth Hire in Manchester',
    eyebrow: '✦ Manchester · Greater Manchester',
    lede:
      'Photobooth hire for weddings, corporate evenings and milestone parties across Greater Manchester — Deansgate, Salford Quays, Trafford, Didsbury, Altrincham, Bolton and Stockport. Booths arrive on time, set up quietly, and leave the night to the people in the room.',
    towns: ['Deansgate', 'Salford Quays', 'Trafford', 'Didsbury', 'Altrincham', 'Bolton', 'Stockport', 'Ashton-under-Lyne'],
    aliases: ['manchester', 'greater manchester', 'salford', 'trafford', 'didsbury', 'altrincham', 'stockport', 'bolton'],
    venuesHeading: 'Venues we attend in Manchester',
  },
  wirral: {
    slug: 'wirral',
    name: 'Wirral',
    title: 'Photobooth Hire on the Wirral — Heswall, West Kirby, Birkenhead ✦ EventDrop',
    metaDescription:
      'Photobooth hire across the Wirral peninsula — Heswall, West Kirby, Birkenhead, Hoylake, Bromborough. Infinity Mirror Booth, Selfie Pod and Audio Guestbook for coastal weddings, milestone birthdays and corporate evenings.',
    headline: 'Photobooth Hire on the Wirral',
    eyebrow: '✦ Wirral · Merseyside peninsula',
    lede:
      'Photobooth hire for coastal weddings, golf-club birthdays and milestone evenings across the Wirral — Heswall, West Kirby, Birkenhead, Hoylake, Bromborough, Caldy and Thornton Hough. Quiet booths for handsome rooms.',
    towns: ['Heswall', 'West Kirby', 'Birkenhead', 'Hoylake', 'Bromborough', 'Caldy', 'Thornton Hough', 'Bebington'],
    aliases: ['wirral', 'heswall', 'birkenhead', 'hoylake', 'west kirby', 'bromborough'],
    venuesHeading: 'Venues we attend on the Wirral',
  },
  shropshire: {
    slug: 'shropshire',
    name: 'Shropshire',
    title: 'Photobooth Hire in Shropshire — Shrewsbury, Telford, Bridgnorth ✦ EventDrop',
    metaDescription:
      'Photobooth hire across Shropshire — Shrewsbury, Telford, Bridgnorth, Oswestry, Ludlow. Rustic Mirror Booth, Infinity Mirror Booth and Audio Guestbook for barn weddings, country-house weddings and milestone celebrations.',
    headline: 'Photobooth Hire in Shropshire',
    eyebrow: '✦ Shropshire · Welsh Marches',
    lede:
      'Photobooth hire for barn weddings, country-house weddings and milestone evenings across Shropshire — Shrewsbury, Telford, Bridgnorth, Oswestry, Ludlow and the Welsh Marches. The Rustic Mirror Booth disappears into Shropshire stone.',
    towns: ['Shrewsbury', 'Telford', 'Bridgnorth', 'Oswestry', 'Ludlow', 'Whitchurch', 'Market Drayton', 'Church Stretton'],
    aliases: ['shropshire', 'shrewsbury', 'telford', 'bridgnorth', 'oswestry', 'ludlow'],
    venuesHeading: 'Venues we attend in Shropshire',
  },
  lancashire: {
    slug: 'lancashire',
    name: 'Lancashire',
    title: 'Photobooth Hire in Lancashire — Preston, Blackburn, Lancaster ✦ EventDrop',
    metaDescription:
      'Photobooth hire across Lancashire — Preston, Blackburn, Lancaster, Burnley, Chorley, the Ribble Valley. Infinity Mirror Booth, Rustic Mirror Booth and Audio Guestbook for country-house weddings, milestone birthdays and corporate evenings.',
    headline: 'Photobooth Hire in Lancashire',
    eyebrow: '✦ Lancashire · North-West England',
    lede:
      'Photobooth hire for country-house weddings, milestone birthdays and corporate evenings across Lancashire — Preston, Blackburn, Lancaster, Burnley, Chorley and the Ribble Valley. Booths styled for the room, no SaaS noise.',
    towns: ['Preston', 'Blackburn', 'Lancaster', 'Burnley', 'Chorley', 'Ribble Valley', 'Clitheroe', 'Garstang'],
    aliases: ['lancashire', 'preston', 'blackburn', 'lancaster', 'burnley', 'chorley', 'ribble valley', 'clitheroe', 'garstang'],
    venuesHeading: 'Venues we attend in Lancashire',
  },
  merseyside: {
    slug: 'merseyside',
    name: 'Merseyside',
    title: 'Photobooth Hire in Merseyside — Liverpool, Wirral, Southport ✦ EventDrop',
    metaDescription:
      'Photobooth hire across Merseyside — Liverpool, Wirral, Southport, St Helens, Bootle, Formby. Infinity Mirror Booth, Selfie Pod and Audio Guestbook for city weddings, dockside corporate evenings and milestone parties.',
    headline: 'Photobooth Hire in Merseyside',
    eyebrow: '✦ Merseyside · Liverpool City Region',
    lede:
      'Photobooth hire for weddings, dockside corporate evenings and milestone parties right across Merseyside — Liverpool, the Wirral peninsula, Southport, St Helens, Bootle and Formby. One operator, one booth, the right styling for the room.',
    towns: ['Liverpool', 'Wirral', 'Southport', 'St Helens', 'Bootle', 'Formby', 'Crosby', 'Birkenhead'],
    aliases: ['merseyside', 'liverpool', 'wirral', 'southport', 'st helens', 'bootle', 'formby', 'crosby', 'birkenhead', 'heswall', 'hoylake'],
    venuesHeading: 'Venues we attend across Merseyside',
  },
};

const matchesRegion = (venue, region) => {
  if (!venue) return false;
  const vRegion = (venue.region || '').toLowerCase();
  const vLoc = (venue.location || '').toLowerCase();
  return region.aliases.some(
    (a) => vRegion === a || vRegion.includes(a) || vLoc.includes(a),
  );
};

const RegionalPhotoboothHirePage = ({ regionKey }) => {
  const region = REGIONS[regionKey];
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [booths, setBooths] = useState([]);
  const [venues, setVenues] = useState([]);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryPackage, setEnquiryPackage] = useState('');

  // iter222i — set <title> and <meta name="description"> per region
  // so each landing page reads as a distinct destination to Google.
  // iter222j — also inject a `LocalBusiness` JSON-LD block so each
  // page can win rich-result placements ("photobooth hire near me",
  // map right-rail) tied to its specific region's areaServed.
  useEffect(() => {
    if (!region) return undefined;
    document.title = region.title;
    const ensureMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    ensureMeta('description', region.metaDescription);

    // Build the LocalBusiness JSON-LD payload. Service-area business
    // pattern (no fixed customer-facing address) — Google guidance
    // recommends `areaServed` as the array of Place objects.
    const origin = (typeof window !== 'undefined' && window.location)
      ? window.location.origin : 'https://eventdrop.co';
    const url = `${origin}/photobooth-hire-${region.slug}`;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': url,
      name: `EventDrop Photobooth Hire — ${region.name}`,
      description: region.metaDescription,
      url,
      image: `${origin}/og-photobooth.jpg`,
      priceRange: '£200–£500',
      serviceType: 'Photobooth Hire',
      areaServed: region.towns.map((t) => ({
        '@type': 'Place',
        name: `${t}, ${region.name}`,
      })),
      provider: {
        '@type': 'Organization',
        name: 'EventDrop',
        url: origin,
      },
      // Service catalogue — three flagship offers per region so
      // Google sees the price range backed by real services.
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Photobooth Hire Packages',
        itemListElement: [
          { '@type': 'Offer', name: 'Digital Party Package', price: '200', priceCurrency: 'GBP' },
          { '@type': 'Offer', name: 'Most Popular Package — Unlimited Prints', price: '350', priceCurrency: 'GBP' },
          { '@type': 'Offer', name: 'Ultimate Celebration Package', price: '500', priceCurrency: 'GBP' },
        ],
      },
    };

    const tagId = 'regional-photobooth-jsonld';
    let scriptEl = document.getElementById(tagId);
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = tagId;
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(jsonLd);

    return () => {
      // Clean up on unmount so the previous region's JSON-LD
      // doesn't leak when the user navigates between regional
      // pages or back to the homepage.
      const stale = document.getElementById(tagId);
      if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
    };
  }, [region]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pkgRes, boothRes, venuesRes] = await Promise.all([
          axios.get(`${API}/services/photobooth/packages`),
          axios.get(`${API}/services/photobooth/booths`),
          axios.get(`${API}/venues`),
        ]);
        if (cancelled) return;
        setPackages(pkgRes.data.packages || []);
        setAddons(pkgRes.data.addons || []);
        setBooths(boothRes.data.booths || []);
        setVenues(venuesRes.data.venues || []);
      } catch {/* render empty */}
    })();
    return () => { cancelled = true; };
  }, []);

  const regionalVenues = useMemo(() => {
    if (!region) return [];
    return venues
      .filter((v) => matchesRegion(v, region))
      .filter((v) => v.image_url || v.cover_image_url)
      .slice(0, 6);
  }, [venues, region]);

  if (!region) return null;

  const openEnquiry = (slug = '') => {
    setEnquiryPackage(slug);
    setEnquiryOpen(true);
  };

  return (
    <div data-testid={`regional-photobooth-${region.slug}`} style={{
      background: PAGE_BG, color: IVORY, fontFamily: SERIF, minHeight: '100vh',
    }}>
      <ServicesNav active="services" />

      {/* Regional hero */}
      <section style={{
        padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 60px) clamp(28px, 4vw, 48px)',
        textAlign: 'center',
      }} data-testid="regional-hero">
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD, marginBottom: 16,
        }}>{region.eyebrow}</div>
        <h1 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(36px, 6vw, 64px)', color: IVORY,
          letterSpacing: '-0.02em', margin: '0 auto', maxWidth: 900,
          lineHeight: 1.08,
        }}>{region.headline}</h1>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.5vw, 18px)', color: IVORY_DIM,
          maxWidth: 720, margin: '24px auto 0', lineHeight: 1.65,
        }}>{region.lede}</p>

        {/* Towns served — quiet chip row, not a marketing wall */}
        <div data-testid="regional-towns" style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
          gap: 8, margin: '28px auto 0', maxWidth: 720,
        }}>
          {region.towns.map((t) => (
            <span key={t} style={{
              padding: '5px 14px', borderRadius: 999,
              border: `1px solid ${GOLD_FAINT}`,
              background: 'rgba(212,170,82,0.06)',
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12, color: IVORY_GHOST,
              letterSpacing: '0.02em',
            }}>{t}</span>
          ))}
        </div>

        {/* CTAs */}
        <div style={{
          display: 'flex', gap: 14, justifyContent: 'center',
          marginTop: 38, flexWrap: 'wrap',
        }}>
          <button
            type="button"
            onClick={() => openEnquiry('')}
            data-testid="regional-cta-check-availability"
            style={PILL_BUTTON_PRIMARY}
          >Check Availability <span style={{ marginLeft: 6, color: '#0F0B1A' }}>✦</span></button>
          <Link
            to="/photobooth-hire"
            data-testid="regional-cta-view-packages"
            style={PILL_BUTTON_GHOST}
          >View all packages →</Link>
        </div>

        {/* iter223 — live availability under the regional hero CTAs */}
        <div style={{ marginTop: 36 }}>
          <AvailabilityWidget />
        </div>
      </section>

      {/* Regional venue strip (only renders when at least one venue has a photo) */}
      {regionalVenues.length > 0 && (
        <section
          data-testid="regional-venue-strip"
          style={{ padding: 'clamp(40px, 5vw, 56px) clamp(20px, 5vw, 60px)' }}
        >
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: GOLD_MUTED,
              marginBottom: 12, textAlign: 'center',
            }}>✦ Local proof</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(24px, 4vw, 34px)', color: IVORY,
              textAlign: 'center', margin: '0 0 28px', letterSpacing: '-0.015em',
            }}>{region.venuesHeading}</h2>
            <div style={{
              display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            }}>
              {regionalVenues.map((v) => (
                <Link
                  key={v.slug}
                  to={`/venues/${v.slug}`}
                  data-testid={`regional-venue-${v.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 10, overflow: 'hidden',
                    textDecoration: 'none', color: IVORY,
                    display: 'flex', flexDirection: 'column',
                    transition: 'transform 320ms ease, border-color 320ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.borderColor = GOLD;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = GOLD_HAIRLINE;
                  }}
                >
                  <BrandedPlaceholder
                    label={v.name}
                    sublabel={v.location}
                    aspectRatio="4 / 3"
                    imageUrl={v.image_url || v.cover_image_url}
                  />
                  <div style={{ padding: '14px 16px 16px' }}>
                    <h4 style={{
                      fontFamily: SERIF, fontWeight: 500, fontSize: 15,
                      margin: '0 0 2px', color: IVORY,
                    }}>{v.name}</h4>
                    <div style={{
                      fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 11.5, color: IVORY_DIM,
                    }}>{v.location}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packages — 4-up grid */}
      <section style={{
        padding: 'clamp(48px, 6vw, 72px) clamp(20px, 5vw, 60px) clamp(40px, 5vw, 56px)',
      }} data-testid="regional-packages">
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: GOLD_MUTED,
          marginBottom: 12, textAlign: 'center',
        }}>✦ Our packages</div>
        <h2 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(28px, 4vw, 40px)', color: IVORY,
          textAlign: 'center', margin: '0 0 36px', letterSpacing: '-0.015em',
        }}>Every booking includes your guests creating magical memories.</h2>
        <div style={{
          maxWidth: 1180, margin: '0 auto',
          display: 'grid', gap: 22,
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        }}>
          {packages.map((pkg) => (
            <RegionalPackageCard
              key={pkg.slug}
              pkg={pkg}
              onEnquire={() => openEnquiry(pkg.slug)}
            />
          ))}
        </div>
      </section>

      {/* Booth Collection */}
      {booths.length > 0 && (
        <section style={{
          padding: 'clamp(30px, 4vw, 48px) clamp(20px, 5vw, 60px)',
        }} data-testid="regional-booths">
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: GOLD_MUTED,
              marginBottom: 12, textAlign: 'center',
            }}>✦ The booth collection</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(24px, 4vw, 32px)', color: IVORY,
              textAlign: 'center', margin: '0 0 28px', letterSpacing: '-0.015em',
            }}>Pick the booth that suits the room.</h2>
            <div style={{
              display: 'grid', gap: 20,
              gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            }}>
              {booths.map((b) => (
                <div
                  key={b.slug}
                  data-testid={`regional-booth-${b.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 10, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                  }}
                >
                  <BrandedPlaceholder
                    label={b.name}
                    sublabel={b.flagship ? 'The flagship ✦' : ''}
                    aspectRatio="4 / 3"
                    imageUrl={b.image_url}
                  />
                  <div style={{ padding: '16px 18px 20px' }}>
                    <h4 style={{
                      fontFamily: SERIF, fontWeight: 500, fontSize: 16,
                      color: IVORY, margin: '0 0 6px',
                    }}>{b.name}</h4>
                    <p style={{
                      fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 13, color: IVORY_DIM,
                      margin: 0, lineHeight: 1.55,
                    }}>{b.tagline}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Add-ons */}
      {addons.length > 0 && (
        <section style={{
          padding: 'clamp(30px, 4vw, 48px) clamp(20px, 5vw, 60px)',
        }} data-testid="regional-addons">
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: GOLD_MUTED,
              marginBottom: 12, textAlign: 'center',
            }}>✦ Add-ons</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(22px, 3.5vw, 30px)', color: IVORY,
              textAlign: 'center', margin: '0 0 28px', letterSpacing: '-0.015em',
            }}>Companion pieces for the night.</h2>
            <div style={{
              display: 'grid', gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            }}>
              {addons.map((a) => (
                <div
                  key={a.slug}
                  data-testid={`regional-addon-${a.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 10, padding: '18px 20px',
                    display: 'flex', flexDirection: 'column',
                  }}
                >
                  <h4 style={{
                    fontFamily: SERIF, fontWeight: 500, fontSize: 16,
                    color: IVORY, margin: '0 0 4px',
                  }}>{a.name}</h4>
                  <p style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 12.5, color: IVORY_DIM,
                    margin: '0 0 12px', lineHeight: 1.55, flex: 1,
                  }}>{a.description}</p>
                  <span style={{
                    fontFamily: SERIF, fontWeight: 600,
                    fontSize: 18, color: GOLD,
                  }}>{a.price ? `£${a.price}` : 'On request'}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section style={{
        padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 60px)',
        textAlign: 'center',
        borderTop: `1px solid ${GOLD_HAIRLINE}`,
        marginTop: 40,
      }}>
        <h2 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(28px, 4vw, 42px)', color: IVORY,
          margin: 0, letterSpacing: '-0.02em',
        }}>Ready to capture {region.name}?</h2>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 15, color: IVORY_DIM,
          maxWidth: 540, margin: '18px auto 28px', lineHeight: 1.6,
        }}>
          Tell us the date and the venue — we&apos;ll confirm availability
          and the right booth for the room.
        </p>
        <button
          type="button"
          onClick={() => openEnquiry('')}
          data-testid="regional-cta-footer"
          style={PILL_BUTTON_PRIMARY}
        >Check Availability <span style={{ marginLeft: 6, color: '#0F0B1A' }}>✦</span></button>
      </section>

      {enquiryOpen && (
        <EnquiryForm
          packages={packages}
          addons={addons}
          initialPackageSlug={enquiryPackage}
          sourcePage={`photobooth-hire-${region.slug}`}
          onClose={() => setEnquiryOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Lightweight regional package card. Reused inline (deliberately
// not imported from PhotoboothHirePage to keep the regional pages
// independently editable as the offer evolves).
const RegionalPackageCard = ({ pkg, onEnquire }) => {
  const isFeatured = !!pkg.featured;
  const coverUrl = pkg.image_url
    ? (pkg.image_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${pkg.image_url}` : pkg.image_url)
    : null;
  return (
    <div
      data-testid={`regional-pkg-${pkg.slug}`}
      style={{
        position: 'relative',
        background: WALNUT_CARD,
        border: `1px solid ${isFeatured ? GOLD : GOLD_HAIRLINE}`,
        borderRadius: 12, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        transform: isFeatured ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isFeatured
          ? '0 22px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,170,82,0.20) inset'
          : '0 12px 36px rgba(0,0,0,0.35)',
      }}
    >
      {isFeatured && (
        <div style={{
          position: 'absolute', top: 12, left: '50%',
          transform: 'translateX(-50%)',
          padding: '5px 14px', borderRadius: 999,
          background: `linear-gradient(135deg, #E8C175 0%, ${GOLD} 100%)`,
          color: '#0F0B1A', fontFamily: SERIF, fontWeight: 700, fontSize: 10.5,
          letterSpacing: '0.20em', textTransform: 'uppercase',
          whiteSpace: 'nowrap', zIndex: 2,
          boxShadow: '0 8px 22px rgba(0,0,0,0.45)',
        }}>{pkg.badge || '⭐ Most Popular'}</div>
      )}
      {coverUrl && (
        <img
          src={coverUrl}
          alt={pkg.name}
          style={{
            width: '100%', aspectRatio: '4 / 3',
            objectFit: 'cover', display: 'block',
            borderBottom: `1px solid ${GOLD_HAIRLINE}`,
          }}
        />
      )}
      <div style={{
        padding: isFeatured && !coverUrl
          ? '46px 22px 24px'
          : '22px 22px 24px',
        display: 'flex', flexDirection: 'column', flex: 1,
      }}>
        <h3 style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 20,
          color: IVORY, margin: '0 0 4px',
        }}>{pkg.name}</h3>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: IVORY_GHOST,
          margin: '0 0 18px', lineHeight: 1.5,
        }}>{pkg.tagline}</p>
        <div style={{
          fontFamily: SERIF, fontWeight: 600, fontSize: 34,
          color: IVORY, marginBottom: 18, letterSpacing: '-0.01em',
        }}>£{pkg.price}</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 22px', flex: 1 }}>
          {(pkg.inclusions || []).slice(0, 5).map((inc, i) => (
            <li key={i} style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              fontFamily: SERIF, fontSize: 13, color: IVORY,
              lineHeight: 1.55, marginBottom: 6,
            }}>
              <span style={{ color: GOLD, flexShrink: 0, marginTop: 2 }}>✦</span>
              <span>{inc}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onEnquire}
          data-testid={`regional-pkg-cta-${pkg.slug}`}
          style={isFeatured ? PILL_BUTTON_PRIMARY : { ...PILL_BUTTON_GHOST, justifyContent: 'center' }}
        >
          {isFeatured ? 'Request a Quote ✦' : 'Enquire Now'}
        </button>
      </div>
    </div>
  );
};

export default RegionalPhotoboothHirePage;
